import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { fetchDeviceCategories, fetchDeviceTypes, fetchPalcareDeviceTypes, publishLearnIn, pollLearnInDevices } from '../api/devicesApi.js';
import { versionSort, iAmHigherAuthority } from '../../../libs.jsx';

// Default Nurse Call category filters (Inovonics)
const NC_AREA_CATEGORY_IDS = new Set([1, 3, 8, 9, 11, 12, 15, 16, 17]);
const NC_RESIDENT_CATEGORY_IDS = new Set([2, 11]);

const DEFAULT_CATEGORY_CONFIG = {
  area: NC_AREA_CATEGORY_IDS,
  resident: NC_RESIDENT_CATEGORY_IDS,
};

// Manufacturer IDs
const INOVONICS_ID = 1;
const PALCARE_ID = 4;

// Serial patterns
const SERIAL_PATTERN_NUMERIC = /^\d+$/;
const SERIAL_PATTERN_ALPHANUM = /^[A-Za-z0-9]+$/;

/**
 * Normalize a PalCare MAC-style serial (device_type_id = 1):
 * lowercase, strip hyphens and colons.  V1 Resident.jsx line 1206.
 */
function normalizePalcareSerial(serial, deviceTypeId) {
  if (String(deviceTypeId) === '1') {
    return serial.toLowerCase().replaceAll('-', '').replaceAll(':', '');
  }
  return serial;
}

/**
 * Get the type-id key for a device-type record.
 * Inovonics types use `inovonics_type_id`; PalCare types use `device_type_id`.
 */
function getTypeId(typeRecord, manufacturerId) {
  return manufacturerId === PALCARE_ID
    ? typeRecord.device_type_id
    : typeRecord.inovonics_type_id;
}

/**
 * AddDeviceDrawer — slide-out drawer for adding a new device.
 *
 * Follows the same pattern as ServerDrawer.jsx: always-render with `is-open`
 * class toggle, form reset on open, Escape key close, focus first input.
 *
 * Supports multiple manufacturers via the `manufacturers` prop. When more than
 * one manufacturer is listed, a toggle is shown at the top. Each manufacturer
 * may have different form behaviour:
 *   - Inovonics (1): area/resident toggle, category, type, model, serial (numeric), alarm behavior
 *   - PalCare  (4): no base toggle (always resident), no category, type, model, serial (alphanumeric), no alarm behavior
 *
 * @param {{
 *   isOpen: boolean,
 *   onClose: () => void,
 *   onSave: (formData: object, transType: string) => Promise<void>,
 *   existingDevices: Array,
 *   campusId: number,
 *   categoryConfig?: { area?: Set<number>, resident?: Set<number> },
 *   title?: string,
 *   manufacturers?: Array<{ id: number, label: string }>,
 *   sessionId?: string,
 *   serverVersion?: string,
 *   roleWeight?: number,
 * }} props
 */
const AddDeviceDrawer = ({
  isOpen,
  onClose,
  onSave,
  existingDevices,
  campusId,
  categoryConfig = DEFAULT_CATEGORY_CONFIG,
  title = 'Add Device',
  manufacturers,
  sessionId = '',
  serverVersion = '',
  roleWeight = 0,
}) => {
  const drawerRef = useRef(null);
  const tipRef = useRef(null);
  const [tipOpen, setTipOpen] = useState(false);
  const [tipStyle, setTipStyle] = useState({});

  // ---- Manufacturer support ----
  const mfrList = useMemo(() => manufacturers || [{ id: INOVONICS_ID, label: 'Inovonics' }], [manufacturers]);
  const showMfrToggle = mfrList.length > 1;
  const defaultMfrId = mfrList[0]?.id ?? INOVONICS_ID;

  const [manufacturerId, setManufacturerId] = useState(defaultMfrId);
  const isPalCare = manufacturerId === PALCARE_ID;

  // Derive available bases from categoryConfig keys (Inovonics only)
  const availableBases = useMemo(() => Object.keys(categoryConfig), [categoryConfig]);
  const showBaseToggle = !isPalCare && availableBases.length > 1;
  const defaultBase = availableBases[0] || 'area';

  // ---- Form state ----
  const [deviceBase, setDeviceBase] = useState(defaultBase);
  const [categoryId, setCategoryId] = useState('');
  const [deviceTypeId, setDeviceTypeId] = useState('');
  const [modelId, setModelId] = useState('');
  const [serialNumber, setSerialNumber] = useState('');
  const [description, setDescription] = useState('');
  const [useId, setUseId] = useState('');
  const [saving, setSaving] = useState(false);

  // ---- Listen-in state (Inovonics only) ----
  const [listening, setListening] = useState(false);
  const [listenCount, setListenCount] = useState(0);
  const [listenData, setListenData] = useState([]);
  const [listenDevice, setListenDevice] = useState('');
  const [listenPCode, setListenPCode] = useState(0);
  const [showDeviceList, setShowDeviceList] = useState(false);

  // ---- Reference data ----
  const [categories, setCategories] = useState([]);
  const [deviceTypes, setDeviceTypes] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [typesLoading, setTypesLoading] = useState(false);

  // ---- Helpers ----
  const resetFormFields = useCallback(() => {
    setCategoryId('');
    setDeviceTypeId('');
    setModelId('');
    setSerialNumber('');
    setDescription('');
    setUseId('');
    // Reset listen-in state
    setListening(false);
    setListenCount(0);
    setListenData([]);
    setListenDevice('');
    setListenPCode(0);
    setShowDeviceList(false);
  }, []);

  const loadCategories = useCallback((base) => {
    const allowedIds = categoryConfig[base];
    if (!allowedIds) return;
    setCategoriesLoading(true);
    fetchDeviceCategories(campusId, base)
      .then((data) => (data || []).filter((c) => allowedIds.has(c.device_category_id)))
      .then((filtered) => setCategories(filtered))
      .catch(() => setCategories([]))
      .finally(() => setCategoriesLoading(false));
  }, [campusId, categoryConfig]);

  const loadDeviceTypes = useCallback((mfrId) => {
    setTypesLoading(true);
    const fetcher = mfrId === PALCARE_ID
      ? fetchPalcareDeviceTypes(campusId)
      : fetchDeviceTypes(campusId);
    fetcher
      .then((data) => setDeviceTypes(data || []))
      .catch(() => setDeviceTypes([]))
      .finally(() => setTypesLoading(false));
  }, [campusId]);

  // ---- Reset + load on open ----
  useEffect(() => {
    if (isOpen) {
      const mfrId = defaultMfrId;
      setManufacturerId(mfrId);
      setDeviceBase(defaultBase);
      resetFormFields();
      setSaving(false);
      setCategories([]);
      setDeviceTypes([]);

      if (mfrId !== PALCARE_ID) {
        loadCategories(defaultBase);
      }
      loadDeviceTypes(mfrId);
    }
  }, [isOpen, campusId, defaultBase, defaultMfrId, loadCategories, loadDeviceTypes, resetFormFields]);

  // ---- Escape key handler ----
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // ---- Focus first input on open ----
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        const firstInput = drawerRef.current?.querySelector('input, select');
        firstInput?.focus();
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // ---- Manufacturer change ----
  const handleManufacturerChange = useCallback((newId) => {
    if (newId === manufacturerId) return;
    setManufacturerId(newId);
    resetFormFields();
    setCategories([]);
    setDeviceTypes([]);

    // Reload reference data for new manufacturer
    if (newId !== PALCARE_ID) {
      setDeviceBase(defaultBase);
      loadCategories(defaultBase);
    }
    loadDeviceTypes(newId);
  }, [manufacturerId, defaultBase, loadCategories, loadDeviceTypes, resetFormFields]);

  // ---- Re-fetch categories when deviceBase changes (Inovonics only) ----
  const handleDeviceBaseChange = useCallback((newBase) => {
    if (newBase === deviceBase) return;
    setDeviceBase(newBase);
    setCategoryId('');
    setDeviceTypeId('');
    setModelId('');
    setUseId('');
    loadCategories(newBase);
  }, [deviceBase, loadCategories]);

  // ---- Reset cascading fields on category change ----
  const handleCategoryChange = useCallback((val) => {
    setCategoryId(val);
    setDeviceTypeId('');
    setModelId('');
    setUseId('');
  }, []);

  // ---- Reset cascading fields on type change ----
  const handleTypeChange = useCallback((val) => {
    setDeviceTypeId(val);
    setModelId('');
    setUseId('');
  }, []);

  // ---- Filtered types ----
  const filteredTypes = useMemo(() => {
    if (isPalCare) {
      // PalCare: show all types (no category filter needed)
      return [...deviceTypes].sort((a, b) =>
        (a.device_type_name || '').localeCompare(b.device_type_name || '')
      );
    }
    // Inovonics: filter by selected category
    if (!categoryId) return [];
    return deviceTypes
      .filter((t) => String(t.device_category_id) === String(categoryId))
      .sort((a, b) => (a.device_type_name || '').localeCompare(b.device_type_name || ''));
  }, [deviceTypes, categoryId, isPalCare]);

  // ---- Selected type's models and uses ----
  const selectedType = useMemo(() => {
    if (!deviceTypeId) return null;
    return deviceTypes.find(
      (t) => String(getTypeId(t, manufacturerId)) === String(deviceTypeId)
    ) || null;
  }, [deviceTypes, deviceTypeId, manufacturerId]);

  const models = useMemo(() => selectedType?.models || [], [selectedType]);

  const uses = useMemo(() => {
    if (isPalCare) return []; // PalCare has no alarm behaviors
    return (selectedType?.uses || []).filter((u) => u.option_number !== 0);
  }, [selectedType, isPalCare]);

  // ---- Auto-select first model when models become available ----
  useEffect(() => {
    if (models.length > 0 && !modelId) {
      setModelId(String(models[0].model_id));
    }
  }, [models, modelId]);

  // ---- Auto-select first use when uses become available ----
  useEffect(() => {
    if (uses.length > 0 && !useId) {
      setUseId(String(uses[0].inovonics_device_use_id));
    }
  }, [uses, useId]);

  // ---- Listen-in: derived values ----
  const selectedModel = useMemo(() => {
    if (!modelId) return null;
    return models.find((m) => String(m.model_id) === String(modelId)) || null;
  }, [models, modelId]);

  const productCode = selectedModel?.product_type_id || 0;
  const msgClass = selectedModel?.message_class || 0;

  const allProductCodes = useMemo(
    () => models.map((m) => m.product_type_id).filter(Boolean),
    [models],
  );

  const listenEnabled = useMemo(() => {
    if (isPalCare) return false;
    return !!modelId && !!sessionId;
  }, [isPalCare, modelId, sessionId]);

  // ---- Listen-in: start handler ----
  const handleStartListen = useCallback(() => {
    if (!listenEnabled) return;
    setListening(true);
    setListenCount(0);
    setListenDevice('');
    setListenPCode(0);
    setListenData([]);
    setShowDeviceList(true);

    // V1 logic: higher authority users send specific productCode, others send all
    const useSpecific =
      iAmHigherAuthority(80, roleWeight) &&
      (serverVersion === '' || versionSort(serverVersion, '4.04.14') >= 0);
    const sendProductType = useSpecific ? [productCode] : allProductCodes;

    publishLearnIn({
      campusId,
      clientId: sessionId,
      productType: sendProductType,
      msgClass,
    }).then(([ok]) => {
      if (!ok) {
        setListening(false);
        setShowDeviceList(false);
      }
    });
  }, [listenEnabled, productCode, allProductCodes, msgClass, campusId, sessionId, roleWeight, serverVersion]);

  // ---- Listen-in: stop handler ----
  const handleStopListen = useCallback(() => {
    setListening(false);
    setShowDeviceList(false);
  }, []);

  // ---- Listen-in: polling timer (60s countdown, poll every 4s) ----
  useEffect(() => {
    if (!listening) return;

    const interval = setInterval(() => {
      setListenCount((prev) => {
        const next = prev + 1;
        if (next >= 60) {
          setListening(false);
          return 60;
        }
        if (next % 4 === 0) {
          pollLearnInDevices({ campusId, clientId: sessionId }).then((data) =>
            setListenData(data || []),
          );
        }
        return next;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [listening, campusId, sessionId]);

  // ---- Listen-in: auto-select first discovered device ----
  useEffect(() => {
    if (listenData.length > 0 && !listenDevice) {
      setListenDevice(String(listenData[0]?.trx_id || ''));
      setListenPCode(listenData[0]?.pType || 0);
    }
  }, [listenData, listenDevice]);

  // ---- Serial validation ----
  const serialPattern = isPalCare ? SERIAL_PATTERN_ALPHANUM : SERIAL_PATTERN_NUMERIC;

  const normalizedSerial = useMemo(() => {
    const trimmed = serialNumber.trim();
    if (!trimmed) return '';
    return isPalCare ? normalizePalcareSerial(trimmed, deviceTypeId) : trimmed;
  }, [serialNumber, isPalCare, deviceTypeId]);

  const isSerialValid = normalizedSerial !== '' && serialPattern.test(normalizedSerial);

  // ---- Duplicate check (uses normalized serial) ----
  const duplicateDevice = useMemo(() => {
    if (!normalizedSerial) return null;
    return (existingDevices || []).find(
      (d) => String(d.manufacture_uuid) === normalizedSerial
    ) || null;
  }, [normalizedSerial, existingDevices]);

  // ---- Form validity ----
  const serialReady = showDeviceList ? listenDevice !== '' : isSerialValid;
  const duplicateReady = showDeviceList ? true : !duplicateDevice;

  const isFormValid =
    (isPalCare || categoryId) &&
    deviceTypeId &&
    modelId &&
    serialReady &&
    (uses.length === 0 || useId) &&
    duplicateReady &&
    !saving;

  // ---- Submit ----
  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      if (!isFormValid) return;
      setSaving(true);

      const isAreaDevice = isPalCare ? false : deviceBase === 'area';
      const transType = isAreaDevice ? 'device_add_area' : 'device_add_resident';

      // Use listened device serial when in listen mode, otherwise manual entry
      const finalSerial = showDeviceList ? listenDevice : normalizedSerial;

      const formData = {
        campus_id: campusId,
        manufacture_id: manufacturerId,
        manufacture_uuid: finalSerial,
        device_name: selectedType?.device_type_name || '',
        device_use_id: useId ? parseInt(useId, 10) : 0,
        is_active: 0,
        is_alerting_now: false,
        is_server: false,
        is_area_device: isAreaDevice ? 1 : 0,
        is_resident: false,
        area_id: 0,
        resident_id: 0,
        manufacture_model_id: parseInt(modelId, 10),
        escalation_enabled: true,
        descriptions: description.trim(),
        device_type_id: parseInt(deviceTypeId, 10),
      };

      onSave(formData, transType).finally(() => setSaving(false));
    },
    [isFormValid, isPalCare, deviceBase, campusId, manufacturerId, normalizedSerial, showDeviceList, listenDevice, selectedType, useId, modelId, description, deviceTypeId, onSave]
  );

  // ---- Sort categories alphabetically ----
  const sortedCategories = useMemo(() =>
    [...categories].sort((a, b) =>
      (a.device_category_short_name || '').localeCompare(b.device_category_short_name || '', undefined, { sensitivity: 'base' })
    ),
  [categories]);

  return (
    <>
      {/* Backdrop */}
      {isOpen ? (
        <div className="dp-drawer-backdrop" onClick={onClose} aria-hidden="true" />
      ) : null}

      {/* Drawer panel */}
      <div
        ref={drawerRef}
        className={`dp-drawer ${isOpen ? 'is-open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        {/* Header */}
        <div className="dp-drawer__header">
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <h2 className="dp-drawer__title">{title}</h2>
            <span
              ref={tipRef}
              onMouseEnter={() => {
                if (!tipRef.current) return;
                const rect = tipRef.current.getBoundingClientRect();
                const spaceRight = window.innerWidth - rect.left;
                const spaceBelow = window.innerHeight - rect.bottom;
                setTipStyle({
                  position: 'fixed',
                  left: spaceRight < 320 ? undefined : `${rect.left}px`,
                  right: spaceRight < 320 ? '16px' : undefined,
                  top: spaceBelow < 120 ? undefined : `${rect.bottom + 6}px`,
                  bottom: spaceBelow < 120 ? `${window.innerHeight - rect.top + 6}px` : undefined,
                  maxWidth: '300px',
                  whiteSpace: 'normal',
                });
                setTipOpen(true);
              }}
              onMouseLeave={() => setTipOpen(false)}
              style={{ cursor: 'help', marginLeft: '8px', color: 'var(--color-text-muted)' }}
            >
              <i className="fe fe-info" style={{ fontSize: 'var(--font-size-sm)' }} />
              {tipOpen && (
                <span className="na-type-popover" style={tipStyle} role="tooltip">
                  <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', fontWeight: 'var(--font-weight-semibold)' }}>Adding Devices</span>
                  <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-body)', lineHeight: '1.45' }}>
                    Devices added to the system are added as inactive. To activate a device it must be assigned to a room or resident, which can be done via the Community &gt; Residents or Community &gt; Areas section of the portal.
                  </span>
                </span>
              )}
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="dp-drawer__close"
            aria-label="Close drawer"
          >
            <svg className="dp-icon-lg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="dp-drawer__body">

          {/* Manufacturer toggle — only when multiple manufacturers */}
          {showMfrToggle ? (
            <div className="dp-drawer__field">
              <label className="dp-drawer__label">Manufacturer <span className="dp-drawer__required">*</span></label>
              <div className="dp-toggle-group">
                {mfrList.map((mfr) => (
                  <button
                    key={mfr.id}
                    type="button"
                    onClick={() => handleManufacturerChange(mfr.id)}
                    className={`dp-toggle-btn ${manufacturerId === mfr.id ? 'is-active' : ''}`}
                  >
                    {mfr.label}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          {/* Device Base toggle (Area / Resident) — Inovonics with multiple bases only */}
          {showBaseToggle ? (
            <div className="dp-drawer__field">
              <label className="dp-drawer__label">Device Base <span className="dp-drawer__required">*</span></label>
              <div className="dp-toggle-group">
                {availableBases.includes('area') ? (
                  <button
                    type="button"
                    onClick={() => handleDeviceBaseChange('area')}
                    className={`dp-toggle-btn ${deviceBase === 'area' ? 'is-active' : ''}`}
                  >
                    Area
                  </button>
                ) : null}
                {availableBases.includes('resident') ? (
                  <button
                    type="button"
                    onClick={() => handleDeviceBaseChange('resident')}
                    className={`dp-toggle-btn ${deviceBase === 'resident' ? 'is-active' : ''}`}
                  >
                    Resident
                  </button>
                ) : null}
              </div>
            </div>
          ) : null}

          {/* Device Category — Inovonics only (PalCare skips this) */}
          {!isPalCare ? (
            <div className="dp-drawer__field">
              <label htmlFor="ad-category" className="dp-drawer__label">
                Device Category <span className="dp-drawer__required">*</span>
              </label>
              <select
                id="ad-category"
                value={categoryId}
                onChange={(e) => handleCategoryChange(e.target.value)}
                className="dp-drawer__select"
                disabled={categoriesLoading}
                required
              >
                <option value="">
                  {categoriesLoading ? 'Loading categories...' : 'Select category...'}
                </option>
                {sortedCategories.map((c) => (
                  <option key={c.device_category_id} value={c.device_category_id}>
                    {c.device_category_short_name}
                  </option>
                ))}
              </select>
            </div>
          ) : null}

          {/* Device Type */}
          <div className="dp-drawer__field">
            <label htmlFor="ad-type" className="dp-drawer__label">
              Device Type <span className="dp-drawer__required">*</span>
            </label>
            <select
              id="ad-type"
              value={deviceTypeId}
              onChange={(e) => handleTypeChange(e.target.value)}
              className="dp-drawer__select"
              disabled={(!isPalCare && !categoryId) || typesLoading}
              required
            >
              <option value="">
                {typesLoading
                  ? 'Loading types...'
                  : !isPalCare && !categoryId
                    ? 'Select a category first...'
                    : 'Select device type...'}
              </option>
              {filteredTypes.map((t) => (
                <option key={getTypeId(t, manufacturerId)} value={getTypeId(t, manufacturerId)}>
                  {t.device_type_name}
                </option>
              ))}
            </select>
          </div>

          {/* Model */}
          <div className="dp-drawer__field">
            <label htmlFor="ad-model" className="dp-drawer__label">
              Model <span className="dp-drawer__required">*</span>
            </label>
            <select
              id="ad-model"
              value={modelId}
              onChange={(e) => setModelId(e.target.value)}
              className="dp-drawer__select"
              disabled={!deviceTypeId || models.length === 0}
              required
            >
              <option value="">
                {!deviceTypeId ? 'Select a device type first...' : 'Select model...'}
              </option>
              {models.map((m) => (
                <option key={m.model_id} value={m.model_id}>
                  {m.model_name}
                </option>
              ))}
            </select>
          </div>

          {/* Serial Number — Manual input or Listen-in dropdown */}
          <div className="dp-drawer__field">
            <label htmlFor="ad-serial" className="dp-drawer__label">
              Serial Number <span className="dp-drawer__required">*</span>
            </label>

            {/* Manual serial input (shown when NOT in listen mode) */}
            {!showDeviceList ? (
              <>
                <input
                  id="ad-serial"
                  type="text"
                  value={serialNumber}
                  onChange={(e) => setSerialNumber(e.target.value)}
                  placeholder={isPalCare ? 'Enter serial number (alphanumeric)' : 'Enter device serial number'}
                  className={`dp-drawer__input ${(serialNumber && !isSerialValid) || duplicateDevice ? 'is-invalid' : ''}`}
                  required={!showDeviceList}
                />
                {serialNumber && !isSerialValid ? (
                  <p className="dp-drawer__error">
                    {isPalCare ? 'Enter a valid alphanumeric serial number' : 'Enter a valid numeric serial number'}
                  </p>
                ) : null}
                {duplicateDevice ? (
                  <p className="dp-drawer__error">A device with this serial number already exists in the system</p>
                ) : null}
              </>
            ) : (
              /* Listen-in device dropdown (shown during/after listen) */
              <select
                id="ad-serial-listen"
                value={listenDevice ? `${listenDevice},${listenPCode}` : ''}
                onChange={(e) => {
                  const parts = e.target.value.split(',');
                  setListenDevice(parts[0] || '');
                  setListenPCode(parseInt(parts[1], 10) || 0);
                }}
                className="dp-drawer__select"
                required={showDeviceList}
              >
                {listenData.length === 0 ? (
                  <option value="">{listening ? 'Listening for devices...' : 'No devices found'}</option>
                ) : (
                  listenData.map((d) => (
                    <option key={d.trx_id} value={`${d.trx_id},${d.pType}`}>
                      {d.name} ({d.trx_id})
                    </option>
                  ))
                )}
              </select>
            )}

            {/* Auto Listen / Stop Listen buttons — Inovonics only */}
            {!isPalCare && sessionId ? (
              <div className="dp-listen-actions">
                {!showDeviceList ? (
                  <button
                    type="button"
                    className="dp-btn dp-btn--sm dp-btn--primary"
                    disabled={!listenEnabled}
                    onClick={handleStartListen}
                    title={!listenEnabled ? 'Select a model first to enable auto listen' : 'Listen for nearby devices'}
                  >
                    <svg className="dp-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9.348 14.652a3.75 3.75 0 010-5.304m5.304 0a3.75 3.75 0 010 5.304m-7.425 2.121a6.75 6.75 0 010-9.546m9.546 0a6.75 6.75 0 010 9.546M5.106 18.894c-3.808-3.808-3.808-9.98 0-13.788m13.788 0c3.808 3.808 3.808 9.98 0 13.788M12 12h.008v.008H12V12zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
                    </svg>
                    Auto Listen
                  </button>
                ) : (
                  <button
                    type="button"
                    className={`dp-btn dp-btn--sm ${listening ? 'dp-btn--danger' : 'dp-btn--secondary'}`}
                    onClick={handleStopListen}
                  >
                    <svg className="dp-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 7.5A2.25 2.25 0 017.5 5.25h9a2.25 2.25 0 012.25 2.25v9a2.25 2.25 0 01-2.25 2.25h-9a2.25 2.25 0 01-2.25-2.25v-9z" />
                    </svg>
                    {listening ? 'Stop Listen' : 'Cancel'}
                  </button>
                )}
              </div>
            ) : null}

            {/* Listen progress bar */}
            {listening ? (
              <div className="dp-listen-progress">
                <div
                  className="dp-listen-progress__bar"
                  style={{ width: `${(listenCount / 60) * 100}%` }}
                />
                <span className="dp-listen-progress__text">
                  {listenData.length > 0
                    ? `${listenData.length} device${listenData.length !== 1 ? 's' : ''} found — ${60 - listenCount}s remaining`
                    : `Listening... ${60 - listenCount}s remaining`}
                </span>
              </div>
            ) : null}
          </div>

          {/* Description (optional) */}
          <div className="dp-drawer__field">
            <label htmlFor="ad-description" className="dp-drawer__label">
              Description
            </label>
            <input
              id="ad-description"
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description"
              className="dp-drawer__input"
            />
          </div>

          {/* Alarm Behavior (uses) — Inovonics only, when type has use options */}
          {uses.length > 0 ? (
            <div className="dp-drawer__field">
              <label className="dp-drawer__label">
                Alarm Behavior <span className="dp-drawer__required">*</span>
              </label>
              {uses.map((u) => (
                <div key={u.inovonics_device_use_id} className="dp-drawer__field--row">
                  <input
                    id={`ad-use-${u.inovonics_device_use_id}`}
                    type="radio"
                    name="ad-alarm-behavior"
                    value={u.inovonics_device_use_id}
                    checked={String(useId) === String(u.inovonics_device_use_id)}
                    onChange={(e) => setUseId(e.target.value)}
                    className="dp-checkbox"
                  />
                  <label htmlFor={`ad-use-${u.inovonics_device_use_id}`} className="dp-drawer__label dp-drawer__label--inline">
                    {u.option_text}
                  </label>
                </div>
              ))}
            </div>
          ) : null}

          {/* Footer */}
          <div className="dp-drawer__footer">
            <button
              type="button"
              onClick={onClose}
              className="dp-btn dp-btn--secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!isFormValid || saving}
              className="dp-btn dp-btn--primary"
            >
              {saving ? 'Adding...' : 'Add Device'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default AddDeviceDrawer;
